import { Injectable, inject, signal, runInInjectionContext, Injector } from '@angular/core';
import { Auth, createUserWithEmailAndPassword, signInWithEmailAndPassword, signOut, sendPasswordResetEmail, User, onAuthStateChanged, UserCredential } from '@angular/fire/auth';
import { Functions, httpsCallable } from '@angular/fire/functions';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { UserService } from '../../services/user.service';
import { AnalyticsService } from '../../services/analytics.service';

@Injectable({
	providedIn: 'root'
})
export class AuthService {
	private auth = inject(Auth);
	private router = inject(Router);
	private injector = inject(Injector);
	private userService = inject(UserService);
	private functions = inject(Functions);
	private analyticsService = inject(AnalyticsService);
	private isSignUpInProgress = false;

	// Signals for reactive state management
	public user = signal<User | null>(null);
	public isLoading = signal<boolean>(true);
	public userEmail = signal<string | null>(null);

	// Observable for auth state initialization
	private authInitializedSubject = new BehaviorSubject<boolean>(false);
	public authInitialized$ = this.authInitializedSubject.asObservable();

	constructor() {
		// Listen to auth state changes
		onAuthStateChanged(this.auth, async (user) => {
			const previousUser = this.user();
			this.user.set(user);
			this.userEmail.set(user?.email || null);
			this.isLoading.set(false);
			this.authInitializedSubject.next(true);

			// Handle user document creation/loading
			if (user) {
				try {
					// Set user ID in analytics
					this.analyticsService.setUserId(user.uid);
					
					// Set the current user in UserService to start listening
					this.userService.setCurrentUser(user);
					
				// Check if user document exists, create if not
				const existingUserDoc = await this.userService.getUserDocument(user.uid);
				if (!existingUserDoc) {
					await this.userService.createUserDocument({
						userId: user.uid,
						email: user.email || '',
						firstName: user.displayName || undefined
					});
				}
				
				// Track sign in
				if (!previousUser || previousUser.uid !== user.uid) {
					this.analyticsService.trackSignIn();
				}
				} catch (error) {
					console.error('Error handling user document:', error);
				}
			} else {
				// Clear user data when signed out
				this.userService.clearUserData();
				// Clear user ID in analytics
				this.analyticsService.setUserId(null);
			}
		});
	}

	/**
	 * Create a new user account with email and password
	 */
	async signUp(email: string, password: string): Promise<void> {
		try {
			this.isSignUpInProgress = true;
			const userCredential: UserCredential = await runInInjectionContext(this.injector, () => {
				return createUserWithEmailAndPassword(this.auth, email, password);
			});
			
			// Track sign up (firebase_id is automatically included via user_properties)
			this.analyticsService.trackSignUp();
			
			// User will be automatically signed in after successful creation
			// Sign in will be tracked in onAuthStateChanged
			
			// Track user in Braze
			try {
				const trackUserInBrazeFunction = runInInjectionContext(this.injector, () => {
					return httpsCallable(this.functions, 'trackUserInBraze');
				});
				
				await trackUserInBrazeFunction({
					emailAddress: email,
					attributes: {
						uid: userCredential.user.uid
					}
				});
			} catch (brazeError: any) {
				// Log Braze tracking error but don't fail signup
				console.error('Error tracking user in Braze:', brazeError);
			}
		} catch (error: any) {
			this.isSignUpInProgress = false;
			throw this.handleAuthError(error);
		}
	}

	/**
	 * Sign in with email and password
	 */
	async signIn(email: string, password: string): Promise<void> {
		try {
			await runInInjectionContext(this.injector, () => {
				return signInWithEmailAndPassword(this.auth, email, password);
			});
			// Sign in tracking will be handled in onAuthStateChanged
		} catch (error: any) {
			throw this.handleAuthError(error);
		}
	}

	/**
	 * Sign out the current user
	 */
	async signOut(): Promise<void> {
		try {
			// Track sign out before clearing user data
			// firebase_id is automatically included via user_properties
			const currentUser = this.user();
			if (currentUser) {
				this.analyticsService.trackSignOut();
			}
			
			// Clear user data first
			this.userService.clearUserData();
			
			await runInInjectionContext(this.injector, () => { 
				return signOut(this.auth);
			});
			this.router.navigate(['/']);
		} catch (error: any) {
			throw this.handleAuthError(error);
		}
	}

	/**
	 * Send password reset email
	 */
	async resetPassword(email: string): Promise<void> {
		try {
			// Configure custom action URL to point to our custom reset page
			const actionCodeSettings = {
				url: `${window.location.origin}/auth/reset-password`,
				handleCodeInApp: false // Must be false for custom email handlers
			};
			
			await runInInjectionContext(this.injector, () => {
				return sendPasswordResetEmail(this.auth, email, actionCodeSettings);
			});
		} catch (error: any) {
			throw this.handleAuthError(error);
		}
	}

	/**
	 * Check if user is currently authenticated
	 */
	isAuthenticated(): boolean {
		const isAuth = this.user() !== null;
		return isAuth;
	}

	/**
	 * Get current user
	 */
	getCurrentUser(): User | null {
		return this.user();
	}

	/**
	 * Get current user document
	 */
	getCurrentUserDocument() {
		return this.userService.currentUser();
	}

	/**
	 * Get user document as observable
	 */
	getUserDocument$() {
		return this.userService.getUserDocument$();
	}


	/**
	 * Handle Firebase authentication errors and return user-friendly messages
	 */
	public handleAuthError(error: any): string {
		switch (error.code) {
		case 'auth/user-not-found':
			return 'No account found with this email address.';
		case 'auth/wrong-password':
			return 'Incorrect password. Please try again.';
		case 'auth/invalid-credential':
			return 'Invalid email or password. Please try again.';
		case 'auth/email-already-in-use':
			return 'An account with this email already exists.';
		case 'auth/weak-password':
			return 'Password should be at least 6 characters long.';
		case 'auth/invalid-email':
			return 'Please enter a valid email address.';
		case 'auth/too-many-requests':
			return 'Too many failed attempts. Please try again later.';
		case 'auth/network-request-failed':
			return 'Network error. Please check your connection.';
		case 'auth/user-disabled':
			return 'This account has been disabled.';
		default:
			return error.message || 'An error occurred. Please try again.';
		}
	}
}
